/* eslint-disable no-console */
import { getRegisteredGlobal } from "@trumpf/ux.utils";
import { logger } from "../../utils/console";
import { verifyLicense } from "./verify-license";

/**
 * We check for a valid ux.ui license key. Every project using ux.ui must obtain a valid license key.
 * License keys can be obtained from the Trumpf UiLib team for free.
 */
export const checkLicense = async (): Promise<void> => {
    if (!isBrowserEnv()) {
        // Skip license check in non-browser environments (SSR, Node.js, etc.)
        return;
    }
    if (!isInternalDomain()) {
        return;
    }
    addEventListener("DOMContentLoaded", async () => {
        const key = await getLicenseKey();
        const isValid = await verifyLicense(key);
        logLicenseMessages(isValid, key);
    });
};

function logLicenseMessages(isValid: boolean, key: string): void {
    if (isValid) {
        logger.debug("ux.ui License key is valid");
        return;
    }
    if (key) {
        logger.error("Your provided ux.ui License key is invalid: " + key);
        return;
    }
    console.error("No ux.ui license key provided. Please provide a valid license key via the ux-license-provider component.");
}

/**
 * Checks if the current environment is a browser.
 */
function isBrowserEnv(): boolean {
    return typeof window !== "undefined" && typeof document !== "undefined";
}

/**
 * Checks if the current hostname matches any internal domain.
 */
function isInternalDomain(): boolean {
    if (!isBrowserEnv()) {
        return false;
    }
    const internalDomains = [".corp.trumpf.com", ".tru", "docs.ux.trumpf.com", "localhost", "127.0.0.1"];
    const hostname = window.location.hostname.toLowerCase();
    return internalDomains.some((domain) => hostname.endsWith(domain));
}

/**
 * Retrieves the license key from the ux-license-provider element, a global variable, or an environment variable.
 */
async function getLicenseKey(): Promise<string> {
    if (!isBrowserEnv()) {
        return "";
    }
    // Try meta tag
    const metaTag = document.querySelector("meta[name='ux-ui-license-key']");
    if (metaTag) {
        return metaTag.getAttribute("content") || "";
    }
    // Try DOM element
    const licenseProvider = document.querySelector("ux-license-provider");
    if (licenseProvider) {
        return licenseProvider.getAttribute("license-key") || "";
    }
    // Try global variable with timeout to ensure it is set
    return getRegisteredGlobal<string>("trumpf.ux.licenseKey") || "";
}
